import { LoyaltyChallengeConfig } from '@shared/models';

import {
  BARCODE_FORMAT,
  DISPLAY_CONDITION,
  ProductCheckTabType,
  RewardFormat,
  ScanCheckMode,
  SubType,
  TitleType,
  Variants,
} from '../utils';

export const BOREAS_CONFIG: LoyaltyChallengeConfig = {
  currency: 'GBP',
  appIcon: 'assets/images/boreas/favicon.ico',
  cssVariables: {
    'font-family-light': 'BootsSharp-Light',
    'font-family-regular': 'BootsSharp-Regular',
    'font-family-bold': 'BootsSharp-Bold',

    'navbar-height': '75px',
    'navbar-background-color': '#F6F6F9',

    'nav-link-color': '#33333F',
    'nav-link-hover-color': '#F6F6F9',
    'nav-link-active-color': '#33333F',
    'nav-link-active-border-color': '#DD1160',

    'nav-item-bg-color': '#F6F6F9',
    'nav-item-active-bg-color': 'white',
    'nav-item-font-family': 'var(--font-family-bold)',
    'nav-item-hover-bg-color': 'rgb(0 0 0 / 10%)',

    'body-color': '#000000',
    'body-background-color': '#ffffff',

    'challenge-primary-color': '#DD1160',

    'tooltip-text-color': 'white',
    'tooltip-background-color': 'black',
  },
  rewardFormat: RewardFormat.PRICE,
  og: {
    title: 'Boots Advantage Extras | Collect up to £35 worth of points',
    description:
      'Collect up to £35 worth of points on your Boots Advantage Card. Choose your Advantage Extras and shop eligible products in store or on boots.com.',
  },
  rewardTemplate: '{points}<span style="font-size: 14px;"></span>',
  pages: {
    availablePages: ['help', 'current', 'history'],
    help: {
      meta: {
        title: 'Boots Advantage Extras — Help & FAQs',
        keywords:
          'Boots, Advantage Card, Advantage Extras, bonus points, loyalty rewards help, Advantage Extras FAQs',
        description:
          'Find help with Boots Advantage Extras — how to choose your Extras, shop eligible products with your Advantage Card, and collect your bonus points.',
      },
      header: {
        background: '#FAE6EB',
        goBackButton: {
          text: 'Go Back To My Challenges',
          backgroundColor: '#DD1160',
          color: 'white',
          fontFamily: 'var(--font-family-regular)',
          borderRadius: '4px',
        },
        bannerAlternativeText: 'Boots Advantage Extras help banner',
        howToPlay: {
          specificDisplayCondition: DISPLAY_CONDITION.ALWAYS,
        },
      },
      faqVariant: {
        variant: Variants.VARIANT_1,
        title: {
          displayWhenCampaignBis: false,
          label: 'Questions',
          type: TitleType.TEXT,
          styles: {
            color: '#33333F',
            fontSize: '28px',
            fontFamily: 'var(--font-family-bold)',
            textAlign: 'left',
            width: '100%',
          },
        },
        questions: [
          {
            title: 'What are Advantage Extras?',
            content:
              'Advantage Extras are personalised bonus points offers available to selected Boots Advantage Card members.<br/><br/>To collect the bonus points shown, buy the qualifying products and complete the spend or purchase targets shown on your progress tracker.<br/><br/>Make sure you complete the offer before the whole Advantage Extras ends, you can check how much time is remaining on the countdown timer.',
          },
          {
            title: 'How can I collect the promised worth of points?',
            content:
              'To collect the promised worth of points, activate 10 available Advantage Extras offers and complete the qualifying purchases needed to collect the maximum points available from each offer during the campaign period.<br/><br/>Because Advantage Extras offers are personalised, the products, spending requirements and points available may be different for each customer.<br/><br/>In the unlikely event that you activate 10 available Advantage Extras offers, complete all the requirements needed to collect the maximum points from each offer, and still collect less than the promised worth of points, we’ll add extra Advantage Card points to bring your reward up to the promised worth of points.',
          },
          {
            title: 'How do I join an Advantage Extras offer?',
            content:
              'When you open the Advantage Extras section in the Boots App, you’ll see personalised offers selected for you.<br/><br/>Simply activate up to 10 offers you’d like to take part in. Then, buy the qualifying products included in each Extra, either in store or online, and we’ll track your progress towards the bonus Advantage Card points on offer.<br/><br/>You can check your progress and see how much time you have left on each offer in the Advantage Extras section.',
          },
          {
            title: 'Do I need an Advantage Card to take part?',
            content:
              'Yes. You must have a valid, registered Boots Advantage Card to take part in Advantage Extras.<br/>To get the best experience, including viewing your personalised Extras, tracking your progress and checking your bonus points collected, we recommend downloading the Boots App and linking your Advantage Card to your account.<br/><br/>To make sure your qualifying purchases are tracked:<ul><li>In Boots stores, scan your Advantage Card every time you shop.</li><li>For online purchases, you must sign in and link your Advantage Card to your boots.com account before placing order. Guest checkout online will not contribute progress to Advantage Extras.</li></ul>Need help linking your Advantage Card? Visit the Boots Advantage Card FAQs page: Advantage Card Help',
          },
          {
            title: 'Will purchases I made before joining the offer count?',
            content:
              'No. Only qualifying purchases made after you’ve selected the offer will count towards your progress.',
          },
          {
            title: 'Where can I see my Advantage Extras progress?',
            content:
              'You can track your offer progress in the Advantage Extras section of the Boots App.<br/><br/>Your progress tracker will show:<ul><li>your progress towards the offer target</li><li>the bonus Advantage Card points available</li><li>how much time you have left to complete the offer</li></ul>',
          },
          {
            title: 'Why hasn’t my progress updated yet?',
            content:
              'Progress updates aren’t always immediate and may take several working days to appear.<br/><br/><ul><li>For purchases made in Boots stores, qualifying purchases will usually appear in your progress tracker within 3 days.</li><li>For online purchases, progress will only update once your order has been dispatched. Depending on delivery and processing times, online purchases will typically be updated within 7 days of order dispatched. However, any order dispatched after the campaign ends won’t qualify for activity progress or bonus points.</li></ul>In some circumstances, updates may take longer due to transaction processing, operational requirements or busy shopping periods.',
          },
          {
            title: 'How is my spend calculated?',
            content:
              'Your progress is based on the amount you exactly pay for qualifying products after discounts and offers are applied.',
          },
          {
            title: 'Do purchases in the Republic of Ireland count?',
            content:
              'No. Purchases made in the Republic of Ireland or on Boots.ie do not contribute towards Advantage Extras offer progress or rewards.',
          },
          {
            title: 'Can I cancel an offer?',
            content:
              'You can cancel an offer at any time before you’ve made progress towards completing it. You can always reactivate the offer later, but any purchases made up before cancellation will not contribute to your total spend.',
          },
          {
            title: 'Do returns or refunds affect my progress?',
            content:
              'Yes.<br/><br/>Returned, cancelled or refunded purchases won’t count towards offer progress.<br/></br>If bonus points have already been issued and a return or refund means the offer requirements are no longer met, Boots may remove the reward or any associated Advantage Card points.',
          },
          {
            title: 'When will I receive my bonus points?',
            content:
              'Once you’ve completed the offer requirements, your qualifying purchases will be validated.<br/><br/>Bonus Advantage Card points are not awarded immediately after each purchase. All bonus points collected from an Advantage Extras are added to your Advantage Card after the promotional period has ended and qualifying purchases have been validated.<br/><br/>Please allow 7 days after the end of the promotional period for bonus points to appear in your Advantage Card account. In some circumstances, bonus points adding may take longer due to transaction processing, system availability or operational factors.',
          },
          {
            title: 'What purchases are excluded?',
            content:
              'Unless stated otherwise in the offer details, the following purchases are excluded:<br/><br/><ul><li>Mobile phone top-ups, infant milks (up to 6 months), items on prescription, postage stamps, gift cards, the plastic bag levy (where applicable), kitchen appliances or applied after purchase</li><li>Services including store ordering, and purchases made in Boots Hearingcare, Boots Opticians, Deliveroo and Uber Eats</li><li>Purchases made by spending Advantage Card points</li></ul>',
          },
          {
            title: 'Why haven’t I received the same Extras as someone else?',
            content:
              'Advantage Extras are personalised and may vary between customers. Not all customers will receive the same Extras.',
          },
          {
            title: 'Where can I find help with my Advantage Card?',
            content:
              'For general Advantage Card questions, please visit the Boots Advantage Card FAQs page: https://www.boots.com/advantage-card/advantage-card-help',
          },
        ],
        accordion: {
          styles: {
            title: {
              fontSize: '16px',
              fontFamily: 'var(--font-family-bold)',
              color: '#33333F',
            },
            description: {
              fontSize: '14px',
              color: '#33333F',
              fontFamily: 'var(--font-family-regular)',
              lineHeight: '20px',
              margin: '0 1rem',
              paddingBottom: '0 !important',
            },
          },
          iconMore: 'assets/images/boreas/expand-more.svg',
          iconLess: 'assets/images/boreas/expand-less.svg',
          alternativeTextExpandMore: 'Expand More',
          alternativeTextExpandLess: 'Expand Less',
        },
      },
      templateBeforeContactForm: [
        '<div style="max-width: 1130px; margin: 1rem auto 3rem; padding: 0 8vw;">',
        '<h2 style="color:#33333F; font-family: var(--font-family-bold); font-size: 28px; margin: 0 0 1rem;">Advantage Extras Terms and Conditions</h2>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">1. The promised worth of points claim is based on a customer activating 10 available Advantage Extras offers and meeting all qualifying purchase requirements necessary to collect the maximum available points from each offer during the campaign period. Offers and rewards are personalised and may vary between customers. If a customer completes the requirements above but collects less than the promised worth of points, Boots will award additional Advantage Card points to bring their total reward to promised worth of points.</p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">2. Advantage Extras are available in stores and on Boots.com and only to eligible Boots Advantage Card members in the UK.</p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">3. Purchases made in the Republic of Ireland or Boots.ie are not included in Advantage Extras progress or point calculations.</p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">4. Purchases made by spending Advantage Card points are not included in Advantage Extras progress or point calculations.</p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">5. Participation is limited and available on a first come, first served basis. An invitation to participate Advantage Extras does not guarantee a place. Customers must select the Advantage Extras while places remain available. Once all places have been allocated, no further customers will be able to join the offer.</p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">6. You must have a valid Boots Advantage Card to participate.</p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">7. Advantage Extras can be accessed through Boots App. For the best experience, including tracking progress, receiving notifications and checking points being collected, customers are encouraged to download the Boots App.</p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">8. Any offers you receive are non-transferable and not exchangeable for cash; alternatives will not be offered.</p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">9. Offers may vary between customers, and not all customers will receive the same.</p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">10. Your Boots Advantage Card must be linked to your Boots account at the time of purchase. Purchases made before a card is linked and before offer being selected will not count towards progress or rewards.</p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">11. To track qualifying purchases:</p><ul style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px; margin: 0;"><li>Instore purchases – your Boots Advantage Card must be registered and scanned at the time of purchase.</li><li>Online purchases – your Boots Advantage Card must be linked to your Boots.com account, and you must be signed in to that account when making your purchase.</li></ul>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px; margin-top: 1rem;">12. Your Advantage Extras progress is based on the amount you pay for qualifying products after any discounts and offers have been applied.</p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">13. An offer can only be cancelled before any qualifying purchase has contributed towards progress. Any purchases made up before cancellation will not contribute to your total spend. </p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">14. Advantage Extras progress updates with the below timeline:</p><ul style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px; margin: 0;"><li>For most in-store purchases, qualifying transactions will usually appear in your offer progress within 3 days of purchase.</li><li>Online purchases will only count towards offer progress once an order has been dispatched and may take 7 days to appear. Orders dispatched after the campaign end date will not count towards offer progress or bonus point rewards, even if the order was placed during the campaign period.</li></ul><p style="color:#33333F; margin-top: 10px;font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">In some circumstances, offer progress updates may take longer due to transaction processing, system availability or operational factors.</p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">15. Progress shown within an Advantage Extras offer does not mean bonus Advantage Card points have been awarded. Bonus points will only be awarded once:</p><ul style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px; margin: 0;"><li>all offer requirements have been met;</li><li>Advantage Extras promotional period has ended; and</li><li>qualifying purchases have been validated.</li></ul><p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;margin-top:10px;">Any bonus Advantage Card points collected through an Advantage Extras will be added to Advantage Card account in 7 days after the end of the promotional period.<br/>In some circumstances, bonus points adding may take longer due to transaction processing, system availability or operational factors.</p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">16. To complete an offer, all qualifying requirements must be met within the promotional period stated in the offer.</p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">17. Boots reserves the right to verify qualifying transactions before awarding any points.</p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">18. Returned, cancelled or refunded purchases will not count towards offer progress. Where a refund, cancellation or return means the offer requirements are no longer met, Boots reserves the right to remove any associated reward or Advantage Card points.</p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">19. Offer progress displayed online or in the Boots App may not immediately reflect refunds, returns or cancelled orders.</p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">20. Unless otherwise stated in the offer details, the following purchases are excluded: Mobile phone top-ups, infant milks (up to 6 months), items on prescription, postage stamps, gift cards, the plastic bag levy (where applicable), kitchen appliances or applied after purchase.  Services including store ordering, and purchases made in Boots Hearingcare, Boots Opticians, Deliveroo and Uber Eats.  Purchases made by spending Advantage Card points are also not incuded.</p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">21. Additional product, brand or service exclusions may apply and will be detailed within the offer.</p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">22. Bonus points have no cash alternative and are non-transferable.</p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">23. Normal pharmacy and legal restrictions apply where applicable.</p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">24. Normal https://www.boots.com/terms-conditions/advantage-card-terms-and-conditions apply</p>',
        '<p style="color:#33333F; font-family: var(--font-family-regular); font-size: 14px; line-height: 24px;">25. Boots reserves the right to withdraw, suspend, amend or cancel an offer where reasonably necessary, including in the event of fraud, abuse, technical issues, administrative errors or circumstances beyond its reasonable control.</p>',
        '</div>',
      ],
    },
    currentChallenges: {
      meta: {
        title: 'Current Boots Advantage Extras | Choose & Collect up to £35',
        keywords:
          'Boots, Advantage Card, Advantage Extras, current offers, bonus points, loyalty rewards, collect £35',
        description:
          'Collect up to £35 worth of points on your Boots Advantage Card. Choose your Advantage Extras and shop eligible products in store or on boots.com.',
      },
      background: 'white',
      howDoesitWork: {
        color: '#C8102E',
        fontFamily: 'var(--font-family-bold)',
        backgroundColor: 'white',
        containerBackgroundColor: '#DD1160',
        text: 'How It Works',
        borderRadius: '4px',
        containerPadding: '0rem 0 2rem',
        modal: {
          background: '#FFFFFF',
          title: {
            text: 'How it works',
            color: '#33333F',
            fontFamily: 'var(--font-family-bold)',
          },
          closeButton: {
            imageUrl: 'assets/images/default/close-button.svg',
            alternativeText: 'Close the tutorial',
            color: '#33333F',
          },
          skipNowButton: {
            text: 'Skip for now',
            color: 'white',
            fontFamily: 'var(--font-family-regular)',
            backgroundColor: '#05054B',
            borderRadius: '4px',
          },
        },
      },
      howToPlay: {
        specificDisplayCondition: DISPLAY_CONDITION.DEFAULT,
      },
    },
    pastChallenges: {
      meta: {
        title: 'Boots Advantage Extras History | Your Past Extras',
        keywords:
          'Boots, Advantage Card, Advantage Extras, history, past offers, completed offers, bonus points, loyalty rewards',
        description:
          'Review the Boots Advantage Extras you have chosen in the past and the points you collected on your Boots Advantage Card.',
      },
      banner: {
        visibility: true,
        title: 'Check your history',
        bannerAriaLabel: 'History page displaying past Boots Advantage Extras',
        background: '#F6F6F9',
        styles: {
          title: {
            margin: 'unset',
            fontSize: '28px',
            color: '#33333F',
            padding: '2rem',
            textAlign: 'left',
            fontFamily: 'var(--font-family-bold)',
          },
          banner: {
            height: '130px',
            marginBottom: '2rem',
          },
        },
      },
      accordion: {
        variant: Variants.VARIANT_1,
        iconMore: 'assets/images/boreas/expand-more.svg',
        iconLess: 'assets/images/boreas/expand-less.svg',
        alternativeTextExpandMore: 'Expand More',
        alternativeTextExpandLess: 'Expand Less',
        icon: {
          imageUrl: 'assets/images/boreas/wallet-card.svg',
          alternativeText: 'Boots Advantage Card icon',
        },
        date: {
          format: 'd MMMM yyyy',
          color: '#33333F',
          fontFamily: 'var(--font-family-bold)',
          template: '{firstDate} - {lastDate}',
        },
        reward: {
          color: '#DD1160',
          fontFamily: 'var(--font-family-bold)',
        },
      },
      noChallengesText: {
        color: '#33333F',
        fontFamily: 'var(--font-family-regular)',
        text: 'No selected challenges',
      },
      noRewardsText: {
        color: '#33333F',
        fontFamily: 'var(--font-family-regular)',
        text: 'No rewards earned',
      },
    },
    sorry: {
      background: '#F2F2F2',
      content: [
        '<div style="max-width: 200px;color: #33333F; text-align: center; margin: 0 auto 1rem;"><img src="assets/images/boreas/sorry-logo.webp" alt=""></div>',
        '<p style="font-size: 1.5rem; font-family: var(--font-family-bold);">Sorry, we couldn’t load the page</p>',
        '<p style="font-size: 1.2rem; font-family: var(--font-family-regular);">Please try again later.</p>',
      ],
    },
  },
  components: {
    header: {
      variant: Variants.VARIANT_14,
      backgroundColor: '#DD1160',
      bannerAriaLabel:
        'Boots Advantage Extras banner, collect up to £35 worth of points',
      backgroundImage: {
        mobile: {
          imageUrl: 'assets/images/boreas/mobile-banner.webp',
        },
        tablet: {
          imageUrl: 'assets/images/boreas/mobile-banner.webp',
        },
        desktop: {
          imageUrl: 'assets/images/boreas/desktop-banner.webp',
        },
      },
      texts: {
        mobile: {
          rewardPrefix: {
            color: '#05054B',
            fontFamily: 'var(--font-family-regular)',
            text: 'Up to',
          },
          rewardAmount: {
            color: '#DD1160',
            fontFamily: 'var(--font-family-bold)',
            text: '£35',
          },
          rewardSuffix: {
            color: '#05054B',
            fontFamily: 'var(--font-family-regular)',
            text: 'worth of<br/><span style="color: #DD1160; font-size: 7vw; font-family: var(--font-family-bold);">points</span>',
          },
          rewardMessage: {
            color: '#05054B',
            fontFamily: 'var(--font-family-regular)',
            text: 'The more you shop,<br/><span style="color: #DD1160;">The more you collect.</span>',
          },
          extrasTitle: {
            color: 'white',
            fontFamily: 'var(--font-family-bold)',
            text: 'Advantage Extras',
            backgroundColor: '#DD1160',
          },
        },
        desktop: {
          title: {
            color: 'white',
            fontFamily: 'var(--font-family-bold)',
            text: 'Advantage Extras',
          },
          rewardPrefix: {
            color: 'white',
            fontFamily: 'var(--font-family-regular)',
            text: 'Up to',
          },
          rewardAmount: {
            color: 'white',
            fontFamily: 'var(--font-family-bold)',
            text: '£35',
          },
          rewardSuffix: {
            color: 'white',
            fontFamily: 'var(--font-family-regular)',
            text: 'worth of points',
          },
          rewardMessage: {
            color: 'white',
            fontFamily: 'var(--font-family-regular)',
            text: 'The more you shop. The more you collect.',
          },
        },
      },
    },
    brands: {
      variant: Variants.VARIANT_1,
      brandsBgColor: 'white',
      border: '2px solid #00529f',
      alternativeText: 'Logos of participating brands',
    },
    footer: {
      variant: Variants.VARIANT_1,
      mobileVisiblity: false,
      styles: {
        display: 'flex',
        padding: '30px 0',
        alignItems: 'center',
        borderTop: '1px solid #f4f5f6',
        backgroundColor: '#33333F',
      },
      linkStyles: {
        color: 'white',
        fontSize: '14px',
        textDecoration: 'none',
        fontFamily: 'var(--font-family-regular)',
      },
      links: [
        {
          url: 'help',
          fragment: 'terms',
          label: '*See T&C’s for full details',
        },
      ],
    },
    tutorial: {
      background: '#FAE6EB',
      carousel: {
        config: {
          sectionAriaLabel: 'Tutorial carousel',
          controlsAriaLabel: 'Carousel controls',
          paginationAriaLabel: 'Slide indicators',
          title: {
            fontSize: '28px',
            color: '#000000',
            text: 'How it works',
            fontFamily: 'var(--font-family-bold)',
            textAlign: 'left',
          },
          dots: {
            inactiveBg: '#C2C2CA',
            activeBg: '#DD1160',
            ariaLabel: 'Go to slide {variable} of {variable}',
          },
          slideIndicatorLabel: 'Slide {variable} of {variable}',
          controls: {
            leftIcon: {
              imageUrl: 'assets/images/boreas/chevron-left.svg',
              alternativeText: 'Go to the previous slide',
            },
            rightIcon: {
              imageUrl: 'assets/images/boreas/chevron-right.svg',
              alternativeText: 'Go to the next slide',
            },
          },
        },
        items: {
          variant: Variants.VARIANT_1,
          styles: {
            title: {
              color: '#05054B',
              fontFamily: 'var(--font-family-bold)',
            },
            description: {
              color: '#33333F',
              fontFamily: 'var(--font-family-light)',
            },
            card: {
              border: 'none',
              background: 'white',
              borderRadius: '16px',
              boxShadow: '0px 4.269px 15.61px 0px rgba(38, 38, 38, 0.15)',
            },
          },
          steps: [
            {
              title: 'Step 1: Select',
              description:
                'Choose up to 10 Advantage Extras you’d like to complete.',
              picture: {
                alternativeText:
                  'An Advantage Extra for sun and holiday products, offering up to 925 points, with an Activate button',
                imageUrl: 'assets/images/boreas/step-1.webp',
              },
            },
            {
              title: 'Step 2: Buy',
              description:
                'Purchase eligible products using your Advantage Card in store, on the Boots mobile app or boots.com.',
              picture: {
                alternativeText: 'A Boots My Advantage Card',
                imageUrl: 'assets/images/boreas/step-2.webp',
              },
            },
            {
              title: 'Step 3: Check',
              description:
                'Track your progress and collect bonus points at the end of each campaign.',
              picture: {
                imageUrl: 'assets/images/boreas/step-3.webp',
                alternativeText: 'A tick, marking a completed Advantage Extra',
              },
            },
            {
              title: 'Step 4: Collect points',
              description:
                'Your bonus points will be added to your Advantage Card 7 days after the end of Advantage Extras.',
              picture: {
                imageUrl: 'assets/images/boreas/step-4.webp',
                alternativeText: 'Up to £35 worth of points',
              },
            },
          ],
        },
      },
    },
    wallet: {
      variant: Variants.VARIANT_6,
      picture: {
        imageUrl: 'assets/images/boreas/wallet-card.svg',
        alternativeText: 'Boots Wallet Card',
      },
      label: {
        color: '#33333F',
        text: 'worth of points collected',
        fontFamily: 'var(--font-family-regular)',
      },
      amount: {
        color: '#DD1160',
        fontFamily: 'var(--font-family-bold)',
      },
    },
    navbar: {
      variant: Variants.VARIANT_1,
      menuVariant: Variants.VARIANT_4,
      hasCountdownInMenu: false,
      goToIcon: {
        alternativeText: '',
        imageUrl: 'assets/images/boreas/go-to-icon.svg',
      },
      fontFamily: 'var(--font-family-bold)',
      dropdownLabel: 'Menu',
      hasLayoutPaddingTop: true,
      navbarAriaLabel: '',
      logo: {
        url: 'current',
        height: '35px',
        imageUrl: 'assets/images/boreas/retailer-logo.svg',
        alternativeText: 'Boots logo',
      },
      openButton: {
        imageAlt: 'Click to see more options',
        buttonTitle: 'Click to see more options',
        imageUrl: 'assets/images/boreas/menu-icon.svg',
      },
      closeButton: {
        imageAlt: 'Close icon',
        buttonTitle: 'Close main menu',
        imageUrl: 'assets/images/boreas/menu-close-icon.svg',
      },
      items: [
        {
          url: 'current',
          label: 'My Extras',
          ariaLabel: 'My Extras',
          menuIcon: {
            alternativeText: 'Home Menu Icon',
            imageUrl: 'assets/images/boreas/home.svg',
          },
          iconUrlSelected: {
            alternativeText: 'Home Menu Icon Selected',
            imageUrl: 'assets/images/boreas/home-selected.svg',
          },
        },
        {
          url: 'history',
          label: 'History',
          ariaLabel: 'My History',
          menuIcon: {
            alternativeText: 'History Menu Icon',
            imageUrl: 'assets/images/boreas/history.svg',
          },
          iconUrlSelected: {
            alternativeText: 'History Menu Icon Selected',
            imageUrl: 'assets/images/boreas/history-selected.svg',
          },
        },
        {
          url: 'help',
          label: 'How it works',
          ariaLabel: 'How it works',
          menuIcon: {
            alternativeText: 'Help Menu Icon',
            imageUrl: 'assets/images/boreas/help.svg',
          },
          iconUrlSelected: {
            alternativeText: 'Help Menu Icon Selected',
            imageUrl: 'assets/images/boreas/help-selected.svg',
          },
        },
      ],
    },
    countdown: {
      variant: Variants.VARIANT_3,
      backgroundColor: '#DD1160',
      showOnPages: 'all',
      labelStyle: {
        color: 'white',
        fontFamily: 'var(--font-family-regular)',
      },
      title: {
        color: 'white',
        fontFamily: 'var(--font-family-regular)',
        label: 'My Extras campaign ends in',
      },
      numberStyle: {
        color: 'white',
        backgroundColor: '#A61E4D',
        fontFamily: 'var(--font-family-regular)',
      },
      texts: {
        days: 'Days',
        hours: 'Hours',
        minutes: 'Minutes',
        seconds: 'Seconds',
      },
    },
    challenges: {
      elements: {
        winCard: {
          variant: Variants.VARIANT_9,
          backgroundColor: 'white',
          completedMsg: {
            color: '#DD1160',
            fontFamily: 'var(--font-family-bold)',
            text: 'Completed!',
          },
          rewardAmount: {
            fontFamily: 'var(--font-family-bold)',
            color: '#DD1160',
            text: 'worth of points.',
          },
          rewardProcessingMessage: {
            fontFamily: 'var(--font-family-regular)',
            color: '#33333F',
            text: 'Your points will be added in 7 days after the end of this Advantage Extras campaign.',
          },
        },

        selectionButton: {
          variant: Variants.VARIANT_8,
          button: {
            color: 'white',
            label: 'Activate',
            backgroundColor: '#DD1160',
            title: 'Activate this challenge',
            fontFamily: 'var(--font-family-regular)',
          },
          offerLabel: {
            color: '#DD1160',
            fontFamily: 'var(--font-family-bold)',
            text: 'Collect {variable} worth of points',
          },
          minimumSpendLabel: {
            color: '#33333F',
            fontFamily: 'var(--font-family-regular)',
            text: 'Minimum spend of £{variable} per visit',
          },
        },
        unselectionButton: {
          variant: Variants.VARIANT_2,
          text: 'Deactivate',
          title: 'Deactivate this challenge',
          color: '#DD1160',
          fontFamily: 'var(--font-family-bold)',
        },
        outOfStock: {
          variant: Variants.VARIANT_1,
          backgroundColor: 'rgba(5, 5, 75, 0.9)',
          borderRadius: '16px',
          description: {
            text: 'Sorry, this Advantage Extra is no longer available.',
            textColor: 'white',
            fontFamily: 'var(--font-family-bold)',
            fontSize: '20px',
          },
        },
      },
      step: {
        [SubType.DEFAULT]: {
          variant: Variants.VARIANT_6,
          bannerAlternativeText: 'Boots Advantage Extras challenge banner',
          logoAlternativeText: 'Logo banner',
          header: {
            height: '143px',
            backgroundColor: '#FFFFFF',
          },
          subHeader: {
            color: '#33333F',
            backgroundColor: 'white',
            completedBackgroundColor: 'white',
            fontFamily: 'var(--font-family-regular)',
            fontSize: '12px',
          },
          body: {
            progressbar: {
              pictureStageReached: {
                imageUrl: 'assets/images/boreas/reward-reached.svg',
                alternativeText: 'Reached reward',
                width: '50px',
              },
              earnLabel: 'Collect',
              collectLabel: '(in points)',
              spendLabel: 'Spend',
              labelColor: '#33333F',
              progressColor: '#DD1160',
              containerColor: '#EBEBEE',
              labelFontFamily: 'var(--font-family-regular)',
            },
            rewards: {
              rewardFontFamily: 'var(--font-family-bold)',
              showReachedIconInBubble: false,
              backgroundColors: {
                bubbleWidth: '50px',
                bubbleHeight: '54px',
                backgroundImage: 'assets/images/boreas/max-reward.svg',
                default: {
                  fill: '#DD1160',
                  stroke: '#DD1160',
                  rewardColor: 'white',
                },
                reached: {
                  fill: '#DD1160',
                  stroke: '#DD1160',
                  rewardColor: 'white',
                },
                unreached: {
                  fill: '#9999A5',
                  stroke: '#9999A5',
                  rewardColor: 'white',
                },
                alternativeText: {
                  reached: 'I earned £{variable}',
                  default: 'I earned £{variable}',
                  unreached: 'Earn £{variable}',
                },
              },
            },
            targets: {
              color: '#33333F',
              fontFamily: 'var(--font-family-bold)',
            },
          },
          footer: {
            backgroundColor: '#FDF3F7',
            unselectLabel: {
              color: '#DD1160',
              fontFamily: 'var(--font-family-bold)',
              label: 'Deactivate',
            },
            totalSpent: {
              color: '#33333F',
              fontFamily: 'var(--font-family-regular)',
              label: 'You’ve spent',
              spentAmountFontFamily: 'var(--font-family-bold)',
            },
            eligibleProductLabel: {
              color: '#05054B',
              fontFamily: 'var(--font-family-bold)',
              label: 'Eligible products',
            },
          },
        },
      },
      frequency: {
        [SubType.DEFAULT]: {
          variant: Variants.VARIANT_11,
          header: {
            backgroundImage: {
              url: 'assets/images/boreas/frequency-banner.webp',
              bannerAriaLabel: 'Boots Advantage Extras visits challenge banner',
            },
            height: '143px',
          },
          subHeader: {
            backgroundColor: 'white',
            completedBackgroundColor: 'white',
            title: {
              color: '#33333F',
              fontFamily: 'var(--font-family-bold)',
              label: 'Shop {variable} times in store or online',
            },
          },
          body: {
            minimumSpendLabel: {
              color: '#33333F',
              fontFamily: 'var(--font-family-regular)',
              text: 'Minimum spend of £{variable} per visit',
            },
            earnLabel: {
              color: '#DD1160',
              text: 'Collect £',
              suffix: 'worth of points',
              fontFamily: 'var(--font-family-bold)',
            },
            stamps: {
              showNumbers: true,
              iconSelected: 'assets/images/boreas/frequency-stamp-success.svg',
              iconNonSelected: 'assets/images/boreas/frequency-stamp-empty.svg',
              nonActiveNumberColor: 'rgba(0, 46, 59, 0.4)',
              activeNumberColor: '#33333F',
            },
            progressBar: {
              activeColor: '#DD1160',
              defaultColor: '#C2C2CA',
            },
            visitsContainer: {
              defaultBorderColor: '#C2C2CA',
              activeBorderColor: '#DD1160',
            },
            footer: {
              backgroundColor: '#FDF3F7',
              remainingVisitsLabel: {
                color: '#05054B',
                text: 'Only {variable} more {visit|visits} to go.',
                fontFamily: 'var(--font-family-bold)',
              },
              unselectLabel: {
                color: '#DD1160',
                fontFamily: 'var(--font-family-bold)',
                text: 'Deactivate',
              },
            },
          },
        },
      },
    },
    challengeSummary: {
      variant: Variants.VARIANT_1,
      stackOnMobile: false,
      textAlignLeft: true,
      title: {
        color: '#33333F',
        text: 'My Extras',
        fontFamily: 'var(--font-family-bold)',
        transform: 'none',
      },
      challengeSelection: {
        text: 'Your purchases may take 3 days* to be shown in our Extras.',
        defaultFontSize: '14px',
        color: '#33333F',
        fontFamily: 'var(--font-family-regular)',
      },
      challengeLabels: {
        onGoingLabel: {
          color: '#33333F',
          text: 'In progress',
          fontFamily: 'var(--font-family-regular)',
        },
        completedLabel: {
          color: '#33333F',
          text: 'Completed',
          fontFamily: 'var(--font-family-regular)',
        },
      },
      counterBorderRadius: '5px',
      challengesInfos: {
        color: '#33333F',
        backgroundColor: '#EBEBEE',
        fontFamily: 'var(--font-family-bold)',
      },
      onGoingChallengesInfo: {
        color: 'white',
        backgroundColor: '#05054B',
        fontFamily: 'var(--font-family-bold)',
      },
      completedChallengesInfo: {
        color: 'white',
        backgroundColor: '#DD1160',
        fontFamily: 'var(--font-family-bold)',
      },
    },
    modals: {
      closeButton: {
        label: 'Close',
        textColor: 'white',
        borderRadius: '5px',
        backgroundColor: '#05054B',
        fontFamily: 'var(--font-family-bold)',
        ariaLabel: 'Close this dialog',
      },
      unselectChallenge: {
        backgroundColor: '#FFFFFF',
        header: {
          backgroundColor: '',
          backgroundImage: '',
        },
        frequencyHeaderImages: {
          [SubType.DEFAULT]: {
            imageUrl: 'assets/images/boreas/frequency-banner.webp',
            alternativeText: '',
          },
        },
        title: {
          color: '#33333F',
          fontFamily: 'var(--font-family-regular)',
          label: 'Deactivate this Advantage Extra?',
        },
        body: {
          color: '#33333F',
          fontFamily: 'var(--font-family-regular)',
          label:
            'You can always reactivate this Advantage Extra later, but any purchases made up until now will not contribute to your total spend.',
        },

        actionButtons: {
          variant: Variants.VARIANT_2,
          confirmButton: {
            borderRadius: '5px',
            textColor: '#FFFFFF',
            backgroundColor: '#DD1160',
            label: 'Deactivate',
            fontFamily: 'var(--font-family-regular)',
            ariaLabel: 'Deactivate this challenge',
          },
          closeButton: {
            label: 'Back',
            borderRadius: '',
            textColor: '#33333F',
            backgroundColor: 'white',
            fontFamily: 'var(--font-family-regular)',
            ariaLabel: 'Back to challenges',
          },
        },
      },
      productCheck: {
        ariaLabel: '',
        backgroundColor: '#FFFFFF',
        fontFamily: 'var(--font-family-regular)',
        tabs: [
          {
            type: ProductCheckTabType.SCAN,
            checkMode: ScanCheckMode.PRODUCT_LIST,
            label: 'Scan for a product',
            title: 'Barcode Verification',
            readers: [BARCODE_FORMAT.EAN_8, BARCODE_FORMAT.EAN_13],
            successMessage:
              'NICE FIND! THIS PRODUCT IS INCLUDED IN ADVANTAGE EXTRAS',
            errorMessage: 'This product is not included',
            startButtonLabel: 'Start scanner',
            retryPermissionButtonLabel: 'Retry camera access',
            failureMessage: 'AN ERROR OCCURRED, PLEASE TRY AGAIN',
            restartButtonLabel: 'Restart Scan',
            errorWhenCameraPermissionDenied:
              'Please allow camera access to use the scanner.',
            errorWhenCameraNotFound: 'Camera not available',
            errorWhenCameraBusy:
              'The camera is in use by another app. Close it, then try again.',
            previewLabel: 'Barcode scanner preview',
            subtitle: 'Scan your product here',
          },
          {
            type: ProductCheckTabType.PRODUCT_LIST,
            failureMessage: 'AN ERROR OCCURRED, PLEASE TRY AGAIN',
            label: 'Enter manually',
            searchable: true,
            inputPlaceholder: 'Enter a barcode to search',
            columns: {
              name: {
                header: 'Product',
                color: '',
                fontFamily: 'var(--font-family-light)',
              },
              code: {
                header: 'Barcode',
                color: '',
                fontFamily: 'var(--font-family-light)',
              },
            },
          },
        ],
        tabConfig: {
          activeColor: 'white',
          inactiveColor: 'black',
          inactiveBackground: 'white',
          fontFamily: 'var(--font-family-regular)',
          inactiveBorderBottom: '2px solid #e6e6e6',
          activeBackground: 'var(--challenge-primary-color)',
          activeBorderBottom: '2px solid var(--challenge-primary-color)',
        },
      },
      firstSelection: {
        backgroundColor: 'white',
        title: {
          color: '#33333F',
          label: 'Yay! You started your<br/>first Advantage Extra',
          fontFamily: 'var(--font-family-regular)',
        },
        header: {
          backgroundImage: '',
          backgroundColor: 'white',
          logo: {
            imageUrl: 'assets/images/boreas/first-selection.svg',
            alternativeText: 'You have selected the first challenge',
          },
        },
        body: {
          color: '#33333F',
          fontFamily: 'var(--font-family-regular)',
          label: 'You can select {variable} more Extras.',
        },
      },
      lastSelection: {
        backgroundColor: 'white',
        title: {
          color: '#33333F',
          fontFamily: 'var(--font-family-regular)',
          label: '{variable} Advantage Extras activated!',
        },
        header: {
          backgroundImage: '',
          backgroundColor: 'white',
          logo: {
            imageUrl: 'assets/images/boreas/last-selection.svg',
            alternativeText: 'You have selected the last challenge',
          },
        },
        body: {
          color: '#33333F',
          fontFamily: 'var(--font-family-regular)',
          label:
            'Well done! You’ve activated 10 Advantage Extras. Collect bonus points by shopping instore or on <a style="color: #33333F; font-family: var(--font-family-regular)" target="_blank" href="https://www.boots.com/">boots.com.</a>',
        },
      },
      maximumSelection: {
        backgroundColor: 'white',
        title: {
          color: '#33333F',
          fontFamily: 'var(--font-family-regular)',
          label: 'You have activated {variable} Advantage Extras!',
        },
        header: {
          backgroundImage: '',
          backgroundColor: 'white',
          logo: {
            imageUrl: 'assets/images/boreas/maximum-selection.svg',
            alternativeText: 'You have selected maximum challenges',
          },
        },
        body: {
          color: '#33333F',
          fontFamily: 'var(--font-family-regular)',
          label: 'Cancel one of your Extras to start this one',
        },
      },
      outOfStockIssue: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#33333F',
          fontFamily: 'var(--font-family-regular)',
          label: 'Limit reached',
        },
        body: {
          color: '#33333F',
          fontFamily: 'var(--font-family-regular)',
          label:
            'This challenge has reached the maximum number of participants. Please choose another.',
        },
      },
      genericError: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#33333F',
          fontFamily: 'var(--font-family-regular)',
          label: 'Error',
        },
        body: {
          color: '#33333F',
          fontFamily: 'var(--font-family-regular)',
          label:
            "An error occurred, and the challenge couldn't be selected. Please check your connection and try again.",
        },
      },
      timeoutError: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#33333F',
          fontFamily: 'var(--font-family-regular)',
          label: 'Request timed out',
        },
        body: {
          color: '#33333F',
          fontFamily: 'var(--font-family-regular)',
          label:
            'Selecting the challenge took too long. Please check your connection and try again.',
        },
      },
      challengeUnavailable: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#33333F',
          fontFamily: 'var(--font-family-regular)',
          label: 'Campaign ended',
        },
        body: {
          color: '#33333F',
          fontFamily: 'var(--font-family-regular)',
          label:
            'This campaign has ended and challenges are no longer available for selection.',
        },
      },
    },
  },
};
